import { memo } from "react";
import Icon from "@/components/ui/Icon";
import { cn } from "@/lib/utils";

type CustomRadioGroupProps = {
  label?: string;
  errorMessage?: string;
  options: string[];
  value: string;
  required?: boolean;
  onChange: (value: string) => void;
} & React.ComponentProps<"div">;

export const CustomRadioGroup = ({
  options,
  value,
  label,
  errorMessage,
  required,
  className,
  onChange,
}: CustomRadioGroupProps) => {
  return (
    <div className="grid grid-cols-1 place-content-start gap-2">
      <div className="grid grid-cols-1 place-content-start gap-2 md:gap-2.5">
        {!!label && (
          <div
            className={cn(
              "text-semantic-text-primary text-base font-normal leading-[130%] md:text-gray-900 md:text-sm md:leading-[20px] md:font-medium",
              {
                "after:content-['*'] after:ml-2 after:text-error": required,
              }
            )}
          >
            {label}
          </div>
        )}
        <div className={cn("grid grid-cols-2 gap-6", className)}>
          {options.map((val) => {
            const valId = val.toLowerCase().replace(/\s+/g, "-");

            return (
              <label key={val} htmlFor={valId} className="relative group">
                <input
                  type="radio"
                  id={valId}
                  name="type"
                  value={val}
                  className="peer sr-only [&:checked~div]:border-blue [&:checked~div]:bg-[rgba(47,128,237,0.05)] [&:checked~div>span]:first:bg-blue-500 [&:checked~div>span]:first:border-blue-500 [&:checked~div>span]:first:text-white"
                  checked={value === val}
                  onChange={() => onChange(val)}
                />
                <div className="bg-white relative rounded-[32px] border border-neutral-200 transition-colors py-[15.2px] px-4 flex items-center cursor-pointer text-base leading-[130%] text-semantic-text-primary md:text-sm md:leading-[100%] group-focus-within:border-blue md:py-[14.2px]">
                  <span className="rounded-full border border-[#666666] transition-colors w-6 h-6 mr-3 shrink-0 flex items-center justify-center text-transparent">
                    <Icon id="icon-check_24" width={24} height={24} />
                  </span>
                  <span className="flex-auto min-w-0 truncate">{val}</span>
                </div>
              </label>
            );
          })}
        </div>
      </div>
      {!!errorMessage && (
        <span className="text-error text-sm">{errorMessage}</span>
      )}
    </div>
  );
};

export default memo(CustomRadioGroup);
